import { api } from "@/redux/api";

import { ExportFileInfo } from "../export/export.schema";

import { ProspectEmailUpdate, ProspectPhoneUpdate } from "../../pages/List/schema/prospect-update";
import {
	BlacklistedContactsRequestParams,
	ContactAddToBlacklistRequest,
	ContactAddToListRequest,
	ContactAddToTrackerRequest,
	ContactAddToTrackerResponse,
	ContactBlacklistActionResponse,
	ContactBlacklistImpactResponse,
	ContactBlacklistRemoveRequest,
	ContactBlacklistedProspect,
	ContactExportRequest,
	ContactListActionResponse,
	ContactProspect,
	ContactProspectOrganization,
	ContactSearchRequest,
	ContactSettings,
	ContactSettingsSchema,
	ContactSnoozeRequest,
	ContactsRequestParams,
	ContactUnsnoozeRequest,
	PaginatedResponse,
	UpdateContactOrganization,
	UpdateContactProspect,
	UpdateContactProspectScoring,
	UpdateContactSettings,
} from "../../pages/Contacts/schema/contacts";
import {
	ContactProspectDetail,
	ContactProspectDetailSchema,
} from "../../pages/Contacts/schema/contact-detail";

export const contactApi = api.injectEndpoints({
	endpoints: (builder) => ({
		getContacts: builder.query<PaginatedResponse<ContactProspect>, ContactsRequestParams>({
			query: ({
				sortBy,
				sortOrder,
				filter,
				searchValue,
				organizationId,
				semanticSearchProspects,
				semanticSearchOrganizations,
				...params
			}) => ({
				url: "contact",
				params: {
					...(params && { ...params }),
					...(sortBy && { sortBy }),
					...(sortOrder && { sortOrder }),
					...(searchValue && { searchValue }),
					...(organizationId && { organizationId }),
					...(filter && { filter }),
					...(semanticSearchProspects && { semanticSearchProspects }),
					...(semanticSearchOrganizations && { semanticSearchOrganizations }),
				},
			}),
			providesTags: ["Contact"],
		}),
		getContactProspectDetail: builder.query<ContactProspectDetail, number>({
			query: (prospectId) => ({
				url: `contact/prospect/${prospectId}`,
				responseSchema: ContactProspectDetailSchema,
			}),
			providesTags: (_result, _error, prospectId) => [{ type: "Contact", id: prospectId }],
		}),
		updateContactProspect: builder.mutation<ContactProspect, UpdateContactProspect>({
			query: ({ id, ...body }) => ({
				url: `contact/prospect/${id}`,
				method: "PATCH",
				body,
			}),
			invalidatesTags: ["Contact"],
		}),
		updateContactOrganization: builder.mutation<
			ContactProspectOrganization,
			UpdateContactOrganization
		>({
			query: ({ id, ...body }) => ({
				url: `contact/organization/${id}`,
				method: "PATCH",
				body,
			}),
			invalidatesTags: ["Contact"],
		}),
		contactSearchForEmails: builder.mutation<ProspectEmailUpdate, ContactSearchRequest>({
			query: (body) => ({
				url: `contact/prospect/searchForEmails`,
				method: "PATCH",
				body,
			}),
		}),
		contactSearchForPhones: builder.mutation<ProspectPhoneUpdate, ContactSearchRequest>({
			query: (body) => ({
				url: `contact/prospect/searchForPhones`,
				method: "PATCH",
				body,
			}),
		}),
		updateContactProspectScoring: builder.mutation<ContactProspect, UpdateContactProspectScoring>({
			query: ({ prospectId, ...body }) => ({
				url: `contact/prospect/${prospectId}/scoring`,
				method: "PATCH",
				body,
			}),
			invalidatesTags: ["Contact"],
		}),
		contactAddToList: builder.mutation<ContactListActionResponse, ContactAddToListRequest>({
			query: (body) => ({
				url: `contact/add-to-list`,
				method: "POST",
				body,
			}),
			invalidatesTags: ["Contact"],
		}),
		contactAddToTracker: builder.mutation<ContactAddToTrackerResponse, ContactAddToTrackerRequest>({
			query: (body) => ({
				url: `contact/add-to-tracker`,
				method: "POST",
				body,
			}),
			invalidatesTags: ["Contact"],
		}),
		getBlacklistedContacts: builder.query<
			PaginatedResponse<ContactBlacklistedProspect>,
			BlacklistedContactsRequestParams
		>({
			query: ({ sortBy, sortOrder, filter, searchValue, ...params }) => ({
				url: "contact/blacklisted",
				params: {
					...(params && { ...params }),
					...(sortBy && { sortBy }),
					...(sortOrder && { sortOrder }),
					...(searchValue && { searchValue }),
					...(filter && { filter }),
				},
			}),
			providesTags: ["Contact"],
		}),
		// Returns 202 with the export row to poll — the file is built off-request and the user is
		// notified when it is ready (PREC-3953).
		requestContactsExport: builder.mutation<ExportFileInfo, ContactExportRequest>({
			query: (body) => ({
				url: `contact/export`,
				method: "POST",
				body,
			}),
		}),
		contactAddToBlacklist: builder.mutation<
			ContactBlacklistActionResponse,
			ContactAddToBlacklistRequest
		>({
			query: (body) => ({
				url: `contact/add-to-blacklist`,
				method: "POST",
				body,
			}),
			invalidatesTags: ["Contact"],
		}),
		// Read-only preview of how disruptive blacklisting the selection would be (how many are on
		// lists / in active campaigns), shown for confirmation before add-to-blacklist commits.
		contactBlacklistImpact: builder.mutation<
			ContactBlacklistImpactResponse,
			ContactAddToBlacklistRequest
		>({
			query: (body) => ({
				url: `contact/blacklist-impact`,
				method: "POST",
				body,
			}),
		}),
		contactRemoveFromBlacklist: builder.mutation<
			ContactBlacklistActionResponse,
			ContactBlacklistRemoveRequest
		>({
			query: (body) => ({
				url: `contact/blacklisted/remove`,
				method: "PATCH",
				body,
			}),
			invalidatesTags: ["Contact"],
		}),
		contactSnoozeProspects: builder.mutation<ContactBlacklistActionResponse, ContactSnoozeRequest>({
			query: (body) => ({
				url: `contact/blacklisted/snooze`,
				method: "PATCH",
				body,
			}),
			invalidatesTags: ["Contact"],
		}),
		contactUnsnoozeProspects: builder.mutation<
			ContactBlacklistActionResponse,
			ContactUnsnoozeRequest
		>({
			query: (body) => ({
				url: `contact/blacklisted/unsnooze`,
				method: "PATCH",
				body,
			}),
			invalidatesTags: ["Contact"],
		}),
		getContactSettings: builder.query<ContactSettings, void>({
			query: () => ({
				url: "contact/settings",
				responseSchema: ContactSettingsSchema,
			}),
			providesTags: ["ContactSettings"],
		}),
		updateContactSettings: builder.mutation<ContactSettings, UpdateContactSettings>({
			query: (body) => ({
				url: "contact/settings",
				method: "PUT",
				body,
				responseSchema: ContactSettingsSchema,
			}),
			invalidatesTags: ["ContactSettings"],
		}),
	}),
});

export const {
	useGetContactsQuery,
	useLazyGetContactsQuery,
	useGetContactProspectDetailQuery,
	useLazyGetContactProspectDetailQuery,
	useGetBlacklistedContactsQuery,
	useLazyGetBlacklistedContactsQuery,
	useUpdateContactProspectMutation,
	useUpdateContactOrganizationMutation,
	useContactSearchForEmailsMutation,
	useContactSearchForPhonesMutation,
	useUpdateContactProspectScoringMutation,
	useContactAddToListMutation,
	useContactAddToTrackerMutation,
	useRequestContactsExportMutation,
	useContactAddToBlacklistMutation,
	useContactBlacklistImpactMutation,
	useContactRemoveFromBlacklistMutation,
	useContactSnoozeProspectsMutation,
	useContactUnsnoozeProspectsMutation,
	useGetContactSettingsQuery,
	useUpdateContactSettingsMutation,
} = contactApi;
